/**
 * Component for populating the data in public `NotificationCorpaySendInstructionPanel` panel
 */

import { Button, Col, Drawer, Form, Select, Modal, Row, Skeleton, Input } from 'antd';
import { get } from 'lodash';
import React, { useState, Suspense, lazy } from 'react';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withDateFormatHandler } from '../../components/common/DateFormatHandler';
import { withNumberFormatHandler } from '../../components/common/NumberFormatHandler';
import { DynamicObject } from '../../utils/commonInterfaces';
import { getIsMobile } from '../../utils/commonFunctions';
import corpayLogo from '../../assets/corpayLogo.png';
const ModalWithSpinner = lazy(
    () => import('../../components/common/ModalWithSpinner')
);

const { Item: FormItem } = Form;
interface IProps {
    readonly visible: boolean;
    readonly setCorpaySendInstructionState: any;
    readonly corpaySendInstructionState: {
        showCorpaySendInstructionPanel: ''
    };
    readonly setSpotRateDetails: any;
    readonly spotRateDetails: {
        spotRate: 0,
        amountToBePaid: 0,
        amountToBeSettled: 0
    };
    readonly paymentId: string;
    readonly company: {
        CompanyId: string,
        Name: string
    };
    readonly customer: {
        Contacts: [
            {
                Email: string
            }
        ];
    };
    readonly sendPaymentInstructionDispatchAction?: (
        payload: any,
        callback?: (response: DynamicObject) => void
    ) => void;
    readonly closePanel?: (refetchList?: boolean) => void;
    readonly form?: any;
}

const NotificationCorpaySendInstructionPanel: React.FC<IProps> = ({
    visible,
    setCorpaySendInstructionState,
    corpaySendInstructionState,
    setSpotRateDetails,
    spotRateDetails,
    paymentId,
    company,
    customer,
    sendPaymentInstructionDispatchAction,
    closePanel,
    form
}: IProps) => {
    const { getFieldDecorator, validateFields, resetFields } = form;
    const { Option } = Select;
    const isMobile = getIsMobile();
    const panelTitle = 'Corpay';
    const dispatch = useDispatch();
    const [submitBtnLoading, setSubmitBtnLoading] = useState<boolean>(false);
    const [cancelDisabled, setCancelDisabled] = useState<boolean>(true);

    /**
     * Function called when drawer or modal is closed.
     */
    const handleClosePanel = () => {
        setSpotRateDetails({});
        setCorpaySendInstructionState({
            ...corpaySendInstructionState,
            showCorpaySendInstructionPanel:
                false,
        });
    };

    /**
     * Function called when submitting the form.
     */
    const handleSubmitForm = () => {
        setSubmitBtnLoading(true);
        validateFields((err: any, values: any) => {
            if (!err) {
                const payload = {
                    "Email": get(values, 'Email'),
                    "CompanyId": company.CompanyId,
                    "PaymentId": paymentId
                }

                if (sendPaymentInstructionDispatchAction) {
                    dispatch(
                        sendPaymentInstructionDispatchAction(payload, (response: DynamicObject) => {
                            getSendPaymentInstructionCallback(response)
                        })
                    );
                } else {
                    if (closePanel) closePanel(true);
                }
            } else {
                setSubmitBtnLoading(false);
            }
        });
    };

    const handleOk = () => {
        setCancelDisabled(false)
    };

    const getSendPaymentInstructionCallback = (
        response: DynamicObject
    ) => {
        if (get(response, 'IsSuccess') === false) {
            setSubmitBtnLoading(false)
            return Modal.error({
                title: 'Error',
                content: get(
                    response,
                    'Messages.0',
                    'Failed to send Payment instruction'
                ),
            });
        } else {
            setSubmitBtnLoading(false)
            Modal.success({
                title: 'Success',
                content: 'Payment instruction email sent successfully.',
                onOk: handleOk,
            });
        }

    }

    const generateSectionWithLoading = (
        sectionComponent: any,
        loadingWidth: number,
        indicatorValue?: any
    ) => {
        if ((!indicatorValue)) {
            return (
                <Skeleton
                    className="notification-skeleton"
                    active={true}
                    loading
                    paragraph={true}
                    title={{ width: loadingWidth }}
                />
            );
        } else {
            return sectionComponent;
        }
    };

    /**
     * Function for populating the panel content (with loading skeletons/data).
     */
    const populatePanelContent = () => {
        var instructEmail = get(customer, 'Contacts[0].Email');
        instructEmail = instructEmail.includes(';') ? instructEmail.substr(0, instructEmail.indexOf(';')) : instructEmail;
        return (
            <>
                <Row>
                    <Row>
                        <img
                            className="corpay-logo-panel"
                            src={corpayLogo}
                            alt="Corpay Logo"
                        />
                    </Row>
                    <br />
                    <Row>
                        The rate has successfully been locked in.
                    </Row>
                    <br />
                    <Row>
                        You are now required to deposit the funds into a Corpay bank account so that the funds can be transferred to {company.Name}.
                    </Row>
                    <br />
                    <Row>
                        <Col>
                            Send payment instructions to this email address:
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <FormItem>
                                {getFieldDecorator('Email', {
                                    initialValue: instructEmail,
                                    rules: [
                                        {
                                            required: true,
                                            message: 'Enter your email address here.',
                                        },
                                        {
                                            type: "email",
                                            message:
                                                'Invalid Email Address!',
                                        },
                                    ],
                                })(
                                    <Input />
                                )}
                            </FormItem>
                        </Col>
                    </Row>
                </Row>
            </>
        );
    }

    return (
        <Row>
            <Col>
                {isMobile ? (
                    <div>
                        {visible && (
                            <Modal
                                className="modal-swapped-buttons modal-no-padding"
                                title={panelTitle}
                                visible={visible}
                                style={{ top: 12 }}
                                footer={false}
                            >
                                <div
                                    style={{
                                        maxHeight: window.innerHeight - 160,
                                        overflowY: 'auto',
                                        padding: 24,
                                    }}
                                >
                                    {
                                        generateSectionWithLoading(
                                            <>
                                                <div>{populatePanelContent()}</div>
                                                <Row>
                                                    <Col className="ta-right" span={24}>
                                                        <Button
                                                            className="mr-8"
                                                            type="primary"
                                                            onClick={handleSubmitForm}
                                                            disabled={submitBtnLoading}
                                                        >
                                                            Send Instructions
                                                        </Button>
                                                        <Button onClick={handleClosePanel}
                                                            disabled={cancelDisabled}
                                                        >
                                                            Close
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </>,
                                            250,
                                            paymentId
                                        )
                                    }
                                </div>
                            </Modal>
                        )}
                    </div>
                ) : (
                    <Drawer
                        title={panelTitle}
                        width={500}
                        onClose={handleClosePanel}
                        visible={visible}
                        className="notification-details-panel"
                        closable={false}
                        maskClosable={false}
                    >

                        {visible && (
                            <>
                                {generateSectionWithLoading(
                                    <>
                                        <div>{populatePanelContent()}</div>
                                        <Row>
                                            <Col className="ta-right" span={24}>
                                                <Button
                                                    className="mr-8"
                                                    type="primary"
                                                    onClick={handleSubmitForm}
                                                    disabled={submitBtnLoading}
                                                >
                                                    Send Instructions
                                                </Button>
                                                <Button onClick={handleClosePanel}
                                                    disabled={cancelDisabled}
                                                >
                                                    Close
                                                </Button>
                                            </Col>
                                        </Row>
                                    </>,
                                    250,
                                    paymentId
                                )}

                            </>
                        )}

                    </Drawer>
                )}
            </Col>
            {submitBtnLoading && (
                <Suspense fallback={null}>
                    <ModalWithSpinner
                        modalTitle="Sending instruction payment email"
                        modalVisible={submitBtnLoading}
                        displayMessage="Please wait while we're sending instruction payment email . . ."
                    />
                </Suspense>
            )}
        </Row>
    );
};

const NotificationPaymentPlanSelectAutoPanelForm = Form.create({
    name: 'notification-corpay-send-instruction-panel-form',
})(NotificationCorpaySendInstructionPanel);

export default withRouter(
    withNumberFormatHandler(
        withDateFormatHandler(NotificationPaymentPlanSelectAutoPanelForm)
    )
);
